import {
  useCallback,
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from 'react'

export type FieldIdentifier = `${string}_${string}`
export type FieldUpdateState = {
  forIndicator: boolean
}

export function buildFieldIdentifier(
  fieldId?: string | null,
  rowId?: string | null,
): FieldIdentifier {
  return `${fieldId || ''}_${rowId || ''}`
}

export type DocumentsChangeSetsContextValue = {
  documentsChangeSetIdsRef: React.MutableRefObject<Record<string, Set<string>>>
  documentLastChangeSetSentAt: Record<string, Date>
  setDocumentLastChangeSetSentAt: React.Dispatch<
    React.SetStateAction<Record<string, Date>>
  >
  fieldsBeingUpdated: Record<FieldIdentifier, FieldUpdateState>
  markFieldsUpdating: (
    fieldIdentifiers: FieldIdentifier[],
    forIndicator: boolean,
  ) => void
  clearAllFieldsBeingUpdated: () => void
  clearFieldsBeingUpdated: (fieldIdentifiers: FieldIdentifier[]) => void
  isFieldUpdating: (fieldId?: string | null, rowId?: string | null) => boolean
  isFieldUpdatingForIndicator: (
    fieldId?: string | null,
    rowId?: string | null,
  ) => boolean
}

const DocumentsChangeSetsContext =
  createContext<DocumentsChangeSetsContextValue>(
    {} as DocumentsChangeSetsContextValue,
  )

export const useDocumentsChangeSetsContext = () =>
  useContext(DocumentsChangeSetsContext)

type DocumentsChangeSetsProviderProps = {
  children: ReactNode
}

export default function DocumentsChangeSetsProvider({
  children,
}: DocumentsChangeSetsProviderProps) {
  const documentsChangeSetIdsRef = useRef<Record<string, Set<string>>>({})

  const [documentLastChangeSetSentAt, setDocumentLastChangeSetSentAt] =
    useState<Record<string, Date>>({})

  const [fieldsBeingUpdated, setFieldsBeingUpdated] = useState<
    Record<FieldIdentifier, FieldUpdateState>
  >({})

  const markFieldsUpdating = useCallback(
    (fieldIdentifiers: FieldIdentifier[], forIndicator: boolean) => {
      if (!fieldIdentifiers.length) return

      setFieldsBeingUpdated((prev) => {
        const next = { ...prev }
        for (const fieldIdentifier of fieldIdentifiers) {
          next[fieldIdentifier] = {
            forIndicator:
              Boolean(next[fieldIdentifier]?.forIndicator) || forIndicator,
          }
        }
        return next
      })
    },
    [],
  )

  const clearAllFieldsBeingUpdated = useCallback(() => {
    setFieldsBeingUpdated((prev) => (Object.keys(prev).length ? {} : prev))
  }, [])

  const clearFieldsBeingUpdated = useCallback(
    (fieldIdentifiers: FieldIdentifier[]) => {
      if (!fieldIdentifiers.length) return
      setFieldsBeingUpdated((prev) => {
        if (!Object.keys(prev).length) return prev
        const next = { ...prev }
        for (const id of fieldIdentifiers) {
          delete next[id]
        }
        return next
      })
    },
    [],
  )

  const isFieldUpdatingForIndicator = useCallback(
    (fieldId: string | null | undefined, rowId: string | null | undefined) => {
      if (!fieldId || !rowId) return false
      const fieldIdentifier = buildFieldIdentifier(fieldId, rowId)
      return Boolean(fieldsBeingUpdated[fieldIdentifier]?.forIndicator)
    },
    [fieldsBeingUpdated],
  )

  const isFieldUpdating = useCallback(
    (fieldId: string | null | undefined, rowId: string | null | undefined) => {
      if (!fieldId || !rowId) return false
      const fieldIdentifier = buildFieldIdentifier(fieldId, rowId)
      return Object.hasOwn(fieldsBeingUpdated, fieldIdentifier)
    },
    [fieldsBeingUpdated],
  )

  return (
    <DocumentsChangeSetsContext.Provider
      value={{
        documentsChangeSetIdsRef,
        documentLastChangeSetSentAt,
        setDocumentLastChangeSetSentAt,
        fieldsBeingUpdated,
        markFieldsUpdating,
        clearAllFieldsBeingUpdated,
        clearFieldsBeingUpdated,
        isFieldUpdating,
        isFieldUpdatingForIndicator,
      }}
    >
      {children}
    </DocumentsChangeSetsContext.Provider>
  )
}
